import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { LoadScript, GoogleMap, Marker } from '@react-google-maps/api';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const AirShipment = ({ shipmentData }) => {
  const renderDetails = () => {
    const { shipmentNumber, awbNumber, status, totalWeight, totalNumberOfPackages, referenceNumber, shipmentLegs, emissions } = shipmentData;
    const airline = shipmentLegs?.portToPort?.carrier || 'N/A';
    const carbonEmissions = emissions?.co2e?.value ? `${emissions.co2e.value} ${emissions.co2e.unit}` : 'N/A';

    return (
      <Box mt={4}>
        <Typography variant="h5" gutterBottom>{shipmentNumber}</Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>AWB No.</strong></TableCell>
                <TableCell><strong>Airline</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Weight</strong></TableCell>
                <TableCell><strong>Piece</strong></TableCell>
                <TableCell><strong>Shipment Ref</strong></TableCell>
                <TableCell><strong>Carbon Emissions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>{awbNumber || 'N/A'}</TableCell>
                <TableCell>{airline}</TableCell>
                <TableCell>{status || 'N/A'}</TableCell>
                <TableCell>{totalWeight ? `${totalWeight} Kg` : 'N/A'}</TableCell>
                <TableCell>{totalNumberOfPackages || 'N/A'}</TableCell>
                <TableCell>{referenceNumber || 'N/A'}</TableCell>
                <TableCell>{carbonEmissions}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderTracking = () => {
    const { shipper, consignee, shipmentLegs } = shipmentData;
    const originAirport = shipmentLegs?.portToPort?.originPort || 'N/A';
    const destinationAirport = shipmentLegs?.portToPort?.destinationPort || 'N/A';
    const originEtd = shipmentLegs?.portToPort?.originPortEtd || 'N/A';
    const destinationEta = shipmentLegs?.portToPort?.destinationPortEta || 'N/A';

    return (
      <Box mt={4}>
        <Typography variant="h6" gutterBottom>Tracking</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TableContainer component={Paper}>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell><strong>Shipper</strong></TableCell>
                    <TableCell>{shipper || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Origin Airport</strong></TableCell>
                    <TableCell>{originAirport}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Origin</strong></TableCell>
                    <TableCell>{shipmentLegs?.portToPort?.originPortCode || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Origin ETD</strong></TableCell>
                    <TableCell>{originEtd.split(' ')[0] || 'N/A'}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12} md={6}>
            <TableContainer component={Paper}>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell><strong>Consignee</strong></TableCell>
                    <TableCell>{consignee || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Destination Airport</strong></TableCell>
                    <TableCell>{destinationAirport}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Destination</strong></TableCell>
                    <TableCell>{shipmentLegs?.portToPort?.destinationPortCode || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Destination ETA</strong></TableCell>
                    <TableCell>{destinationEta.split(' ')[0] || 'N/A'}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderSegments = () => {
    const segments = shipmentData.shipmentLegs?.portToPort?.segments || [];
    if (segments.length === 0) return null;

    return (
      <Box mt={4}>
        <Typography variant="h6" gutterBottom>Shipping Segments</Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Flight</strong></TableCell>
                <TableCell><strong>Origin Airport</strong></TableCell>
                <TableCell><strong>Destination Airport</strong></TableCell>
                <TableCell><strong>Flight #</strong></TableCell>
                <TableCell><strong>Departure</strong></TableCell>
                <TableCell><strong>Arrival</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {segments.map((segment, index) => (
                <TableRow key={index}>
                  <TableCell>{segment.transportName || 'N/A'}</TableCell>
                  <TableCell>{segment.origin || segment.originPortCode || 'N/A'}</TableCell>
                  <TableCell>{segment.destination || segment.destinationPortCode || 'N/A'}</TableCell>
                  <TableCell>{segment.tripNumber || 'N/A'}</TableCell>
                  <TableCell>{segment.etd ? segment.etd.split(' ')[0] : segment.atd ? segment.atd.split(' ')[0] : 'N/A'}</TableCell>
                  <TableCell>{segment.eta ? segment.eta.split(' ')[0] : segment.ata ? segment.ata.split(' ')[0] : 'N/A'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderMap = () => {
    const { latitude, longitude } = shipmentData.shipmentLegs?.portToPort?.latestLocation || {};
    if (!latitude || !longitude) return null;

    // return (
    //   <Box mt={4}>
    //     <Typography variant="h6" gutterBottom>Current Location</Typography>
    //     <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
    //       <GoogleMap
    //         mapContainerStyle={{ width: '100%', height: '400px' }}
    //         center={{ lat: parseFloat(latitude), lng: parseFloat(longitude) }}
    //         zoom={10}
    //       >
    //         <Marker position={{ lat: parseFloat(latitude), lng: parseFloat(longitude) }} />
    //       </GoogleMap>
    //     </LoadScript>
    //   </Box>
    // );
  };

  return (
    <>
      {renderDetails()}
      {renderTracking()}
      {renderSegments()}
      {renderMap()}
    </>
  );
};

export default AirShipment;